import { cn } from '@src/design-system/helpers';

import { type ComponentProps } from 'react';
import { EditableCell } from './editable-cell';
import {
  Command,
  CommandList,
  CommandInput,
  CommandEmpty,
} from '@src/design-system/command';
import { SelectContentCountry } from '../fields/country-field/memoized-options';
import { useGetAllLocales } from '../services/get-all-locales';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@src/design-system/dropdown-menu';
import { ArrowSquareDown } from 'iconsax-react';
import {
  StateBoundary,
  StateBoundaryContent,
  StateBoundaryError,
  StateBoundaryLoading,
} from '@src/design-system/state-boundary';
import { Spinner } from '@src/design-system/spinner';

export function EditableCountryCell({ ...props }: ComponentProps<'div'>) {
  return (
    <DropdownMenu>
      <EditableCell {...props} />
    </DropdownMenu>
  );
}

export function EditableCountryCellEditMode({
  ...props
}: ComponentProps<typeof CountryList>) {
  return (
    <DropdownMenuContent
      align='start'
      className='p-2 rounded-lg max-w-[200px] border border-pale-border!'
      alignOffset={-8}
    >
      <CountryList {...props} />
    </DropdownMenuContent>
  );
}

const CountryList = ({
  ...props
}: ComponentProps<typeof SelectContentCountry>) => {
  const { data: countries, isPending, isError } = useGetAllLocales();

  return (
    <Command>
      <CommandInput
        className='rounded-sm'
        placeholder='Search by name...'
      />
      <CommandList style={{ scrollbarWidth: 'none' }}>
        <StateBoundary
          isLoading={isPending}
          isError={isError}
          isNotFound={!countries}
        >
          {!isPending && <CommandEmpty>No results found.</CommandEmpty>}

          <StateBoundaryError>
            An error occurred while fetching the countries.
          </StateBoundaryError>

          <StateBoundaryLoading className='h-[100px]'>
            <Spinner className='size-6' />
          </StateBoundaryLoading>

          <StateBoundaryContent>
            <SelectContentCountry
              countries={countries}
              {...props}
            />
          </StateBoundaryContent>
        </StateBoundary>
      </CommandList>
    </Command>
  );
};

export function EditableCountryCellViewMode({
  className,
  children,
  ...props
}: ComponentProps<typeof DropdownMenuTrigger>) {
  return (
    <DropdownMenuTrigger
      className={cn(
        'group flex items-center justify-between text-start w-full h-full cursor-pointer',
        className
      )}
      {...props}
    >
      <span className='truncate'>{children}</span>

      <ArrowSquareDown
        size={16}
        className='text-primary-1000 shrink-0 group-hover:block hidden'
      />
    </DropdownMenuTrigger>
  );
}
